import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  FileText, 
  Scale, 
  Mail, 
  ExternalLink,
  Shield,
  Sparkles,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  Info
} from "lucide-react";
import { getTermsOfServiceSEO, validateSEOData, generateKeywords } from "@/services/seoService";

const TermsOfService = () => {
  const [totalNavHeight, setTotalNavHeight] = useState(112); // Default navbar height

  // Listen for navigation height updates from NavBar
  useEffect(() => {
    const handleNavHeightChange = (event: CustomEvent) => {
      setTotalNavHeight(event.detail.height);
    };

    // Listen for height updates from NavBar
    window.addEventListener('navHeightUpdate', handleNavHeightChange as EventListener);

    return () => {
      window.removeEventListener('navHeightUpdate', handleNavHeightChange as EventListener);
    };
  }, []);

  const sections = [
    {
      id: "introduction",
      title: "1. Introduction",
      icon: "📋",
      content: `Welcome to JamPack'd Care Packages. These Terms of Service ("Terms") govern your access to and use of our website, services, and products (collectively, the "Service"). By accessing or using the Service, you agree to be bound by these Terms. If you do not agree, you may not use the Service.`
    },
    {
      id: "eligibility",
      title: "2. Eligibility",
      icon: "🔞",
      content: `You must be at least 18 years old to use the Service. By placing an order or creating an account, you represent and warrant that you meet this age requirement.`
    },
    {
      id: "account-registration",
      title: "3. Account Registration",
      icon: "👤",
      subsections: [
        {
          title: "3.1. Account Creation",
          content: "To place orders or access certain features, you must register for an account. You agree to provide accurate, complete, and up-to-date information."
        },
        {
          title: "3.2. Account Security",
          content: "You are responsible for safeguarding your password and account. You agree not to share your account credentials or allow unauthorized use. You must notify us immediately of any breach of security or unauthorized use."
        }
      ]
    },
    {
      id: "orders-payment",
      title: "4. Orders and Payment",
      icon: "💳",
      subsections: [
        {
          title: "4.1. Order Acceptance",
          content: "All orders are subject to acceptance and availability. We reserve the right to refuse or cancel any order for any reason."
        },
        {
          title: "4.2. Payment Methods",
          content: "We accept payments via Stripe payment elements and, where available, Affirm, Afterpay, or Klarna for orders over USD 35. You agree to pay all charges associated with your order."
        },
        {
          title: "4.3. Prices and Taxes",
          content: "Prices are displayed in your selected currency and do not include applicable taxes, duties, or shipping fees. You are responsible for all such charges."
        }
      ]
    },
    {
      id: "shipping-delivery",
      title: "5. Shipping and Delivery",
      icon: "🚚",
      subsections: [
        {
          title: "5.1. Carriers and Rates",
          content: "We ship worldwide using DHL and Jamaica Post. Shipping rates are calculated at checkout based on weight, dimensions, and destination. Free standard shipping applies to orders over USD 79."
        },
        {
          title: "5.2. Customs and Duties",
          content: "International orders may be subject to customs fees, import duties, and taxes, which are the responsibility of the recipient."
        }
      ]
    },
    {
      id: "returns-refunds",
      title: "6. Returns and Refunds",
      icon: "↩️",
      subsections: [
        {
          title: "6.1. Return Window",
          content: "You may request a return within 30 days of delivery for unopened items."
        },
        {
          title: "6.2. Return Shipping",
          content: "You are responsible for return shipping costs unless the item is defective."
        },
        {
          title: "6.3. Refund Processing",
          content: "After we receive and inspect your return, we will process your refund within 5–7 business days. Refunds will be issued to the original payment method."
        },
        {
          title: "6.4. Non-Returnable Items",
          content: "Perishable goods, custom gift sets, and clearance items are not eligible for return."
        }
      ]
    },
    {
      id: "prohibited-uses",
      title: "7. Prohibited Uses",
      icon: "🚫",
      content: `You agree not to:

• Use the Service for any illegal purpose or in violation of any law.
• Introduce viruses, malware, spam, or other harmful code.
• Infringe any third party's intellectual property, privacy, or publicity rights.
• Harass, threaten, or discriminate against others.
• Scrape, data-mine, or commercially exploit any portion of the Service without our prior written permission.`
    },
    {
      id: "intellectual-property",
      title: "8. Intellectual Property",
      icon: "©️",
      content: `All content on the Service, including text, graphics, logos, images, and software, is the property of JamPack'd Care Packages or its licensors and is protected by copyright and other laws. All rights are reserved. You are granted a limited, revocable, non-exclusive license to access, view, and print pages from the Service for your personal, non-commercial use. Any other use requires our express written permission.`
    },
    {
      id: "disclaimer-warranties",
      title: "9. Disclaimer of Warranties",
      icon: "⚠️",
      content: `THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY. JAMPACK'D DISCLAIMS ALL IMPLIED WARRANTIES, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.`
    },
    {
      id: "limitation-liability",
      title: "10. Limitation of Liability",
      icon: "🛡️",
      content: `TO THE MAXIMUM EXTENT PERMITTED BY LAW, JAMPACK'D'S TOTAL LIABILITY ARISING OUT OF OR IN CONNECTION WITH THESE TERMS OR THE SERVICE SHALL NOT EXCEED THE AMOUNT PAID BY YOU FOR THE ORDER GIVING RISE TO THE CLAIM. JAMPACK'D SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, OR EXEMPLARY DAMAGES.`
    },
    {
      id: "indemnification",
      title: "11. Indemnification",
      icon: "🤝",
      content: `You agree to indemnify, defend, and hold harmless JamPack'd Care Packages, its officers, directors, employees, agents, and affiliates from and against any claims, liabilities, damages, losses, and expenses (including reasonable legal fees) arising out of or in any way connected with (a) your breach of these Terms, (b) your use of the Service, or (c) your violation of any law or the rights of any third party.`
    },
    {
      id: "governing-law",
      title: "12. Governing Law and Venue",
      icon: "🏛️",
      content: `These Terms and any dispute arising from or relating to them are governed by the laws of Jamaica. You agree that any legal action or proceeding must be brought exclusively in the courts located in Saint Andrew, Jamaica.`
    },
    {
      id: "modifications",
      title: "13. Modifications to Terms",
      icon: "📝",
      content: `We may update these Terms at any time by posting the revised version on our website. Your continued use of the Service after any changes constitutes acceptance of the new Terms. We recommend reviewing these Terms periodically.`
    },
    {
      id: "termination",
      title: "14. Termination",
      icon: "🔚",
      content: `We may suspend or terminate your access to the Service at our sole discretion, without notice, for conduct that we believe violates these Terms or is harmful to other users or us.`
    },
    {
      id: "miscellaneous",
      title: "15. Miscellaneous",
      icon: "📄",
      subsections: [
        {
          title: "15.1. Entire Agreement",
          content: "These Terms constitute the entire agreement between you and JamPack'd regarding the Service and supersede all prior or contemporaneous communications."
        },
        {
          title: "15.2. Severability",
          content: "If any provision of these Terms is found invalid or unenforceable, the remaining provisions will remain in full force and effect."
        },
        {
          title: "15.3. No Waiver",
          content: "Our failure to enforce any right or provision of these Terms will not constitute a waiver of future enforcement of that right or provision."
        },
        {
          title: "15.4. Assignment",
          content: "You may not assign or transfer these Terms without our prior written consent. We may assign these Terms at any time."
        }
      ]
    },
    {
      id: "contact",
      title: "16. Contact Information",
      icon: "📧",
      content: `For questions or notices under these Terms, please contact:

JamPack'd Care Packages
35A Upper Waterloo Road
Kingston 8, Saint Andrew
Jamaica
Email: support@jampackd.store`
    }
  ];

  // Generate SEO data for Terms of Service page
  const seoData = validateSEOData(getTermsOfServiceSEO());

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="author" content={seoData.author} />
        
        {/* Open Graph Tags for Social Sharing */}
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:image" content={seoData.image} />
        <meta property="og:url" content={seoData.url} />
        <meta property="og:type" content={seoData.type} />
        <meta property="og:site_name" content={seoData.siteName} />
        <meta property="og:locale" content={seoData.locale} />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta name="twitter:image" content={seoData.image} />
        
        {/* Additional SEO Tags */}
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={seoData.url} />
      </Helmet>
      
      <div 
        className="flex flex-col"
        style={{ minHeight: `calc(100vh - ${totalNavHeight}px)` }}
      >
      <main className="flex-grow relative bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Floating Geometric Shapes */}
          <div className="absolute top-20 left-20 w-16 h-16 bg-gradient-to-br from-jamaica-green/10 to-jamaica-green/5 rounded-2xl transform rotate-45 animate-pulse delay-300"></div>
          <div className="absolute top-40 right-32 w-12 h-12 bg-jamaica-yellow/15 rounded-full animate-bounce delay-500"></div>
          <div className="absolute bottom-32 left-1/4 w-20 h-20 bg-gradient-to-br from-jamaica-yellow/8 to-jamaica-green/8 rounded-3xl transform rotate-12 animate-ping delay-700"></div>
          <div className="absolute bottom-20 right-20 w-14 h-14 bg-jamaica-green/12 rounded-lg transform -rotate-12 animate-pulse delay-900"></div>
          
          {/* Triangle Decorative Elements */}
          <div className="absolute top-1/4 right-10 w-0 h-0 border-l-[20px] border-r-[20px] border-b-[35px] border-l-transparent border-r-transparent border-b-jamaica-green/10 animate-pulse delay-1000"></div>
          <div className="absolute bottom-1/3 left-16 w-0 h-0 border-l-[15px] border-r-[15px] border-b-[26px] border-l-transparent border-r-transparent border-b-jamaica-yellow/15 animate-bounce delay-1200"></div>
          
          {/* Grid Pattern */}
          <div className="absolute inset-0 opacity-30">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                linear-gradient(rgba(0, 116, 47, 0.05) 1px, transparent 1px),
                linear-gradient(90deg, rgba(0, 116, 47, 0.05) 1px, transparent 1px)
              `,
              backgroundSize: '50px 50px'
            }}></div>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Enhanced Hero Section */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-2xl flex items-center justify-center shadow-xl transform rotate-6">
                  <Scale className="w-6 h-6 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 w-5 h-5 bg-jamaica-yellow rounded-full flex items-center justify-center animate-bounce">
                  <Shield className="w-2.5 h-2.5 text-white" />
                </div>
              </div>
              <div className="bg-gradient-to-r from-jamaica-green/10 to-jamaica-yellow/10 px-4 py-1.5 rounded-2xl border border-jamaica-green/20 shadow-lg">
                <span className="text-sm font-bold text-jamaica-black flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-jamaica-green" />
                  Legal Information
                </span>
              </div>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 leading-tight">
              <span className="bg-gradient-to-r from-gray-900 via-jamaica-green to-gray-900 bg-clip-text text-transparent p-1">
                Terms of Service
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-6">
              Please read these terms carefully before using our 
              <span className="text-jamaica-yellow font-semibold"> JamPack'd service</span>. 
              By using our platform, you agree to these 
              <span className="text-jamaica-green font-semibold"> terms and conditions</span>
            </p>

            {/* Important Notice */}
            <div className="max-w-2xl mx-auto">
              <div className="relative backdrop-blur-sm bg-amber-50/80 border-2 border-amber-200 rounded-2xl shadow-lg p-4 hover:shadow-xl transition-all duration-500">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center mt-0.5">
                    <Info className="w-4 h-4 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-amber-800 mb-1">Important Notice</p>
                    <p className="text-sm text-amber-700">
                      These terms were last updated on July 24, 2025 and are effective as of the date of your access to our service. 
                      By continuing to use JamPack'd, you acknowledge that you have read and agree to these terms.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
        
        {/* Terms Content */}
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            {sections.map((section, index) => (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="relative bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
                  {/* Background decoration for each card */}
                  <div className="absolute inset-0 bg-gradient-to-r from-jamaica-green/3 via-transparent to-jamaica-yellow/3"></div>
                  
                  <CardContent className="relative p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
                        <span className="text-xl">{section.icon}</span>
                      </div>
                      <div className="flex-grow">
                        <h2 className="text-xl font-bold text-gray-900 mb-2">{section.title}</h2>
                        {section.content && (
                          <div className="prose prose-gray max-w-none">
                            <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                              {section.content}
                            </p>
                          </div>
                        )}
                        
                        {section.subsections && (
                          <div className="space-y-4 mt-4">
                            {section.subsections.map((subsection, subIndex) => (
                              <div key={subIndex} className="pl-4 border-l-2 border-jamaica-green/20">
                                <h3 className="font-semibold text-gray-900 mb-2">{subsection.title}</h3>
                                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                                  {subsection.content}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
          
          {/* Enhanced Contact Support Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-12"
          >
            <Card className="relative bg-gradient-to-br from-jamaica-green/5 to-jamaica-yellow/5 border-0 shadow-xl backdrop-blur-sm overflow-hidden">
              {/* Background decoration */}
              <div className="absolute inset-0 bg-gradient-to-r from-jamaica-green/5 via-transparent to-jamaica-yellow/5"></div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-jamaica-yellow/10 rounded-full -translate-y-16 translate-x-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-jamaica-green/10 rounded-full translate-y-12 -translate-x-12"></div>
              
              <CardContent className="relative p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-2xl mb-6 shadow-lg">
                  <Mail className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Questions about these Terms?</h2>
                <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                  If you have any questions or concerns about these Terms of Service, our team is here to help. 
                  Contact us and we'll get back to you as soon as possible.
                </p>
                <div className="flex flex-col gap-4 items-center justify-center">
                  <Button asChild className="bg-gradient-to-r from-jamaica-green to-jamaica-green/90 text-white hover:from-jamaica-green/90 hover:to-jamaica-green shadow-lg hover:shadow-xl transition-all duration-300 px-6 py-3 rounded-xl">
                    <a href="mailto:support@jampackd.store" className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Contact Support
                      <ArrowRight className="h-4 w-4" />
                    </a>
                  </Button>
                  <div className="text-sm">
                    {/* Mobile chips */}
                    <div className="grid grid-cols-1 gap-2 px-4 sm:hidden">
                      <div className="flex items-center gap-2 justify-center rounded-full border border-green-200 bg-green-50/60 text-gray-700 px-3 py-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span className="font-medium">Legal questions welcome</span>
                      </div>
                      <div className="flex items-center gap-2 justify-center rounded-full border border-green-200 bg-green-50/60 text-gray-700 px-3 py-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span className="font-medium">24-hour response time</span>
                      </div>
                    </div>
                    {/* Desktop original layout */}
                    <div className="hidden sm:flex items-center gap-6 text-gray-500">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span>Legal questions welcome</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span>24-hour response time</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </main>
    </div>
    </>
  );
};

export default TermsOfService;